import * as React from 'react'
import { Slot } from 'radix-ui'

import { cn } from '@/lib/utils'
import styles from './IconButton.module.scss'

export type IconButtonSize = 'xs' | 'sm' | 'md' | 'lg'

const sizeClass: Record<IconButtonSize, string> = {
  xs: styles.xs,
  sm: styles.sm,
  md: styles.md,
  lg: styles.lg,
}

/**
 * Single-icon square button. Use for toolbar actions, popover/dropdown
 * triggers, and any other icon-only interactive control.
 *
 * Sizes: xs (24px) · sm (28px) · md (34px, default) · lg (40px)
 *
 * Pass `asChild` when wrapping a Radix trigger (PopoverTrigger, DropdownMenuTrigger, etc.)
 * so the trigger merges its props onto this element rather than nesting a button.
 */
export function IconButton({
  size = 'md',
  asChild = false,
  className,
  ...props
}: React.ComponentProps<'button'> & {
  size?: IconButtonSize
  asChild?: boolean
}) {
  const Comp = asChild ? Slot.Root : 'button'

  return (
    <Comp
      type={asChild ? undefined : 'button'}
      data-slot="icon-button"
      data-size={size}
      className={cn(styles.iconButton, sizeClass[size], className)}
      {...props}
    />
  )
}
